import { z } from "zod";
import { OPTIONAL_AND_CONTINUED_OFFICE_CODE } from "../constants/constants";

/**
 * 記号は最大5桁で、空文字列は意図的に許容
 * zeroPaddingが有効で足りなければ左0埋め
 * trimReadingZerosが有効であれば左0埋めを削除
 */
export const officeCodeSchema = (
  params?: { zeroPadding?: boolean } | { trimReadingZeros?: boolean },
) => {
  const s = z
    .string()
    .regex(/^\d*$/, { error: "数値で入力してください" })
    .max(5, { error: "最大5桁で入力してください" });

  if (params) {
    if ("zeroPadding" in params && params.zeroPadding) {
      return s.transform((v) => v.padStart(5, "0"));
    }
    if ("trimReadingZeros" in params && params.trimReadingZeros) {
      return s.transform((v) => {
        // "0"のみから構成される記号は"0"になる
        // 動作確認用事業所向けの特殊対応
        if (v !== "" && /^0+$/.test(v)) {
          return "0";
        }
        return v.replace(/^0+/, "");
      });
    }
  }

  return s;
};

/**
 * 被保険者番号は最大8桁で、空文字列は意図的に許容
 * zeroPaddingが有効で足りなければ左0埋め
 * trimReadingZerosが有効であれば左0埋めを削除
 *
 * 被保険者番号全体を指してinsurance_numberと呼ぶこともあるが、これは「番号」と呼ばれるもの
 */
export const insuranceNumberSchema = (
  params?:
    | {
        zeroPadding?: boolean;
      }
    | { trimReadingZeros?: boolean },
) => {
  const s = z
    .string()
    .regex(/^\d*$/, { error: "数値で入力してください" })
    .max(8, { error: "最大8桁で入力してください" });

  if (params) {
    if ("zeroPadding" in params && params.zeroPadding) {
      return s.transform((v) => v.padStart(8, "0"));
    }
    if ("trimReadingZeros" in params && params.trimReadingZeros) {
      return s.transform((v) => v.replace(/^0+/, ""));
    }
  }

  return s;
};

// 枝番は2桁固定
const branchNumberSchema = z
  .string()
  .regex(/^\d{2}$/, { error: "数値2桁で入力してください" });

/**
 * 保険証番号（記号・番号・枝番を区切り文字で繋いだものの呼称。デフォルトはハイフンだが delimiter オプションで変更可能）を入力に取って、バリデーションしつつ各部分に分割するスキーマ
 */
export const insuranceCardNumberSchema = (
  params?: { delimiter?: string | RegExp } & (
    | {
        zeroPadding?: boolean;
      }
    | { trimReadingZeros?: boolean }
  ),
) =>
  z
    .string()
    .transform((rawString) => rawString.split(params?.delimiter ?? "-"))
    .pipe(
      z
        .array(z.string())
        .length(3, {
          error: "「記号-番号-枝番」の形式で入力してください",
        })
        .pipe(
          z.tuple([
            officeCodeSchema(params),
            insuranceNumberSchema(params),
            branchNumberSchema,
          ]),
        )
        .transform(([officeCode, insuranceNumber, branchNumber]) => {
          return {
            officeCode,
            insuranceNumber,
            branchNumber,
          };
        }),
    );

/**
 * 保険証番号をフォーマットする
 *
 * CSVから取得した保険証番号の各部分を適切にゼロパディングして
 * データベース保存形式（XXXXX-XXXXXXXX-XX）に変換する
 *
 * @param officeCode 記号（5桁）
 * @param insuranceNumber 被保険者番号（8桁）
 * @param branchNumber 枝番（2桁固定）
 * @returns フォーマット済み保険証番号（XXXXX-XXXXXXXX-XX）、バリデーションエラーの場合はnull
 */
export function formatInsuranceNumber(
  officeCode: string,
  insuranceNumber: string,
  branchNumber: string,
): string | null {
  const r = z
    .object({
      officeCode: officeCodeSchema({ zeroPadding: true }),
      insuranceNumber: insuranceNumberSchema({ zeroPadding: true }),
      branchNumber: branchNumberSchema,
    })
    .safeParse({
      officeCode,
      insuranceNumber,
      branchNumber,
    });
  if (!r.success) {
    return null;
  }
  const d = r.data;

  return `${d.officeCode}-${d.insuranceNumber}-${d.branchNumber}`;
}

// 表示用（左0埋め削除）のスキーマは呼び出しごとに変わらないため、生成コストを避けてモジュールスコープで保持する
const OFFICE_CODE_TRIM_READING_ZEROS_SCHEMA = officeCodeSchema({
  trimReadingZeros: true,
});
const INSURANCE_NUMBER_TRIM_READING_ZEROS_SCHEMA = insuranceNumberSchema({
  trimReadingZeros: true,
});

/**
 * 保険証番号を表示用の記号・番号・枝番に分割する
 *
 * データベース保存形式（XXXXX-XXXXXXXX-XX）を検証しつつ、
 * 記号・被保険者番号の左0埋めを削除した各部分に分解する
 *
 * @param formattedNumber フォーマット済み保険証番号（XXXXX-XXXXXXXX-XX）
 * @returns 分解した記号・番号・枝番。不正なフォーマットの場合はnull
 * @example
 * // "00123-00000456-02" → { officeCode: "123", insuranceNumber: "456", branchNumber: "02" }
 * // "12345-123" → null
 */
export function splitInsuranceNumberForDisplay(formattedNumber: string): {
  officeCode: string;
  insuranceNumber: string;
  branchNumber: string;
} | null {
  // ハイフンで分割
  const parts = formattedNumber.split("-");

  if (parts.length !== 3) {
    return null;
  }

  const [officeCode, insuranceNumber, branchNumber] = parts;

  // 記号と被保険者番号は、スキーマで数値文字列であることを検証しつつ左0埋めを削除する
  const validatedOfficeCode =
    OFFICE_CODE_TRIM_READING_ZEROS_SCHEMA.safeParse(officeCode);
  const validatedInsuranceNumber =
    INSURANCE_NUMBER_TRIM_READING_ZEROS_SCHEMA.safeParse(insuranceNumber);
  const validatedBranchNumber = branchNumberSchema.safeParse(branchNumber);

  if (
    !validatedOfficeCode.success ||
    // ここでは空文字列はinvalidとして扱う
    !validatedOfficeCode.data ||
    !validatedInsuranceNumber.success ||
    !validatedInsuranceNumber.data ||
    !validatedBranchNumber.success
  ) {
    return null;
  }

  // 記号が"0"のみから構成される場合は"0"になる（動作確認用事業所向けの特殊対応）
  return {
    officeCode: validatedOfficeCode.data,
    insuranceNumber: validatedInsuranceNumber.data,
    branchNumber: validatedBranchNumber.data,
  };
}

/**
 * 保険証番号の表示用フォーマット（左0埋めを削除）
 *
 * データベース保存形式（XXXXX-XXXXXXXX-XX）から
 * 表示用形式（左0埋めなし）に変換する
 *
 * @param formattedNumber フォーマット済み保険証番号（XXXXX-XXXXXXXX-XX）
 * @returns 表示用保険証番号（左0埋めなし）
 * @example
 * // "12345-00000001-01" → "12345-1-01"
 * // "00123-00000456-02" → "123-456-02"
 */
export function formatInsuranceNumberForDisplay(
  formattedNumber: string,
): string {
  const parts = splitInsuranceNumberForDisplay(formattedNumber);

  if (parts === null) {
    // 不正なフォーマットの場合はそのまま返す
    return formattedNumber;
  }

  return `${parts.officeCode}-${parts.insuranceNumber}-${parts.branchNumber}`;
}

/**
 * 保険番号が任意継続（記号9999）かどうかを判定
 *
 * @param formattedNumber 保険証番号（DB保存形式 "XXXXX-XXXXXXXX-XX" / 表示形式 "X-X-XX" のどちらでも可）
 * @returns 任意継続の場合true
 * @example
 * // DB保存形式: "09999-00000001-01" → true
 * // 表示形式: "9999-1-01" → true
 * // "00123-00000456-02" → false
 * // "123-456-02" → false
 */
export function isOptionalInsuranceNumber(formattedNumber: string): boolean {
  const [officeCode] =
    formatInsuranceNumberForDisplay(formattedNumber).split("-");
  return officeCode === OPTIONAL_AND_CONTINUED_OFFICE_CODE;
}
